import { FLOW_TYPE } from 'consts/flows';
import orderBy from 'lodash/orderBy';
import type {
	Answer,
	FORMELEMENT,
	QuestionGroup,
	Questionnaire,
	QuestionProps,
	RiskChoice,
	RiskProfile,
	RiskQuestion
} from 'types';

const IGNORED_INPUT_TYPES = new Set(['button', 'submit', 'reset']);
const GROUPED_INPUT_TYPES = new Set(['radio', 'checkbox']);
const CHOICE_BOOL = new Set(['já', 'nei']);
const CHOICE_INLINE = new Set([
	...CHOICE_BOOL,
	'engin',
	'enginn',
	'góð',
	'lítil',
	'miðlungs',
	'nokkur',
	'rangt',
	'rétt'
]);

export function IsBoolChoice(choices: RiskChoice[]) {
	return 2 === choices.length && choices.every(c => CHOICE_BOOL.has(c.caption.toLowerCase()));
}

export function IsInline(choices: RiskChoice[]) {
	return choices.length && choices.every(c => CHOICE_INLINE.has(c.caption.toLowerCase()));
}

export function isRiskLevelAllowed(current?: string, testing?: string) {
	const currentLevel = RiskLevel(current);
	const testingLevel = RiskLevel(testing);
	if (Number.isNaN(currentLevel) || Number.isNaN(testingLevel)) {
		console.error("Can't determinate RiskLevel");
		return false;
	}
	return testingLevel <= currentLevel;
}

function RiskLevel(str = '') {
	return str.codePointAt(str.length - 1) ?? Number.NaN;
}

export function isQuestionVisible(props: QuestionProps, riskq: Answer[]) {
	const { dependencyChoiceIds, dependsOnQuestionId, testId } = props;
	if (null === dependsOnQuestionId) return true;
	return dependencyChoiceIds.some(id => {
		return riskq.find(a => {
			return id === a.choiceId && dependsOnQuestionId === a.questionId && testId === a.testId;
		});
	});
}

export function buildAnswer(testId: number, questionId: number, choiceId: number): Answer {
	return { questionId, choiceId, testId, textValue: null };
}

export function nextBTNLabel(flowType: string, isLastPage: boolean, t: (key: string) => string) {
	if (isLastPage) return t('submit_btn_label');
	if (FLOW_TYPE.RR_SU === flowType) return t('rr-su_continue');
	return t('continue');
}

export function getDropdownValue(choices: RiskChoice[], answer?: Answer) {
	const index = choices.findIndex(c => c.id === answer?.choiceId);
	return -1 === index ? '' : index;
}

export function hasLabel(recommendedId: number, profileId: number, riskProfile: RiskProfile) {
	return recommendedId === profileId || riskProfile?.id === profileId;
}

export function transformRiskq(riskq: Questionnaire) {
	return {
		...riskq,
		questionGroups: riskq.questionGroups.reduce((acc: QuestionGroup[], group: QuestionGroup) => {
			const questions = orderBy(group.questions, 'ordering', 'asc');
			questions.forEach(sortQuestions);
			acc.push({ ...group, questions });
			return acc;
		}, [])
	};
}

export function getRiskqSSNPath(isRRSU: boolean, contextKennitala?: string, userKennitala?: string) {
	if (isRRSU) {
		return contextKennitala ? `/${contextKennitala}` : '';
	}
	return userKennitala ? `/${userKennitala}` : '';
}

export function IsDisabled() {
	const wrapper = document.querySelector('.container.page-container');
	if (!wrapper) return true;

	const controls = Array.from<FORMELEMENT>(wrapper.querySelectorAll('input, select, textarea'));

	if (!controls.length) return true;

	const groupedCheckState: Record<string, boolean> = {};

	for (let index = 0; index < controls.length; index++) {
		const control = controls[index];
		if (control.disabled) continue;
		if (!isControlFilled(control, index, groupedCheckState)) return true;
	}

	return Object.values(groupedCheckState).some(isChecked => !isChecked);
}

function isControlFilled(control: FORMELEMENT, index: number, groupedCheckState: Record<string, boolean>) {
	if (!(control instanceof HTMLInputElement)) return hasTextValue(control);
	if (IGNORED_INPUT_TYPES.has(control.type)) return true;
	if (GROUPED_INPUT_TYPES.has(control.type)) {
		addGroupedInputState(groupedCheckState, control, index);
		return true;
	}
	if ('file' === control.type) return !!control.files?.length;
	return hasTextValue(control);
}

function addGroupedInputState(
	groupedCheckState: Record<string, boolean>,
	control: HTMLInputElement,
	index: number
) {
	const key = control.name || `__control_${index}`;
	groupedCheckState[key] = groupedCheckState[key] || control.checked;
}

function hasTextValue(control: FORMELEMENT) {
	return !!control.value.trim();
}

function sortQuestions(question: RiskQuestion) {
	question.subQuestions = orderBy(question.subQuestions, 'ordering', 'asc');
	question.choices.sort(sortChoices);
}

function sortChoices(a: RiskChoice, b: RiskChoice) {
	const x = Number(a.ordering);
	const y = Number(b.ordering);
	if (x < y) return -1;
	if (x > y) return 1;
	return 0;
}
